import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { FilterChips, SearchBar, SpecialistCard, CategoryGrid } from '@/components/domain';
import {
  Button,
  CityPicker,
  EmptyState,
  Spinner,
  readStoredCitySlug,
} from '@/components/atoms';
import { useBackButton, useDebouncedValue } from '@/hooks';
import { catalog, specialists, savedSearches } from '@/api';
import { mockCategories } from '@/mocks/categories';
import { mockSpecialists } from '@/mocks/specialists';
import type { Category, SpecialistSummary } from '@/types/domain';
import { logger } from '@/utils/logger';
import { toast } from '@/components/atoms/Toast';
import styles from './pages.module.css';

// F-170 B3.2: location filter ('Будь-де' default). Sends
// `?location=online|delivery|onsite` to the backend search; the FE
// option is the source of truth for the chip-row state.
type LocationFilter = '' | 'online' | 'delivery' | 'onsite';

const LOCATION_OPTIONS: { value: LocationFilter; label: string }[] = [
  { value: '', label: 'Будь-де' },
  { value: 'online', label: 'Онлайн' },
  { value: 'delivery', label: 'З доставкою' },
  { value: 'onsite', label: 'Виїзд' },
];

export function SearchPage(): JSX.Element {
  useBackButton();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialQ = params.get('q') ?? '';
  const [q, setQ] = useState(initialQ);
  const [location, setLocation] = useState<LocationFilter>('');
  // F-170 B3.4: city — URL `?city=` overrides the persisted localStorage
  // pick so deep-link results match exactly what the URL asked for. Both
  // writes flow back through localStorage.
  const [citySlug, setCitySlug] = useState<string | null>(
    () => params.get('city') || readStoredCitySlug(),
  );
  const [results, setResults] = useState<SpecialistSummary[] | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  // F-180 C14: 300ms debounce so the backend search fires while typing
  // without spamming a request on every keystroke.
  const debouncedQ = useDebouncedValue(q, 300);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await catalog.fetchCategories();
        if (!cancelled) setCategories(data.results ?? mockCategories);
      } catch {
        if (!cancelled) setCategories(mockCategories);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const runSearch = useCallback(
    async (query: string): Promise<void> => {
      const trimmed = query.trim();
      if (!trimmed) {
        setResults(null);
        return;
      }
      setResults(null);
      try {
        const data = await specialists.searchSpecialists({
          q: trimmed,
          location: location || null,
          city: citySlug ? [citySlug] : undefined,
        });
        setResults(data.results);
      } catch (err) {
        logger.warn('Search failed', err);
        setResults(
          mockSpecialists.filter((s) =>
            s.first_name.toLowerCase().includes(trimmed.toLowerCase()) ||
            s.bio_excerpt.toLowerCase().includes(trimmed.toLowerCase()),
          ),
        );
      }
    },
    [location, citySlug],
  );

  // F-170 B3.2 / B3.4 + F-180 C14: re-run search when the debounced
  // query, location chip, or city flips so the user gets results as
  // they type. Skip when the (debounced) query is empty.
  useEffect(() => {
    if (debouncedQ.trim()) void runSearch(debouncedQ);
    else setResults(null);
  }, [debouncedQ, location, citySlug, runSearch]);

  const saveSearch = async (): Promise<void> => {
    if (!q.trim()) return;
    try {
      await savedSearches.createSavedSearch({
        name: q,
        filters: { q },
        delivery_mode: 'instant',
      });
      toast.success('Збережено. Алерти увімкнено.');
    } catch (err) {
      logger.warn('Save search failed', err);
      toast.error('Не вдалось зберегти');
    }
  };

  return (
    <Layout title="Пошук">
      <SearchBar value={q} onChange={setQ} onSubmit={() => void runSearch(q)} />
      {/* F-170 B3.4: city dropdown shares state with HomePage via
          localStorage. Sits on its own row to give it room next to the
          location chips below. */}
      <div style={{ marginTop: 8 }}>
        <CityPicker value={citySlug} onChange={setCitySlug} />
      </div>
      {/* F-170 B3.2: location-kind filter row. Single-select chip — the
          four options are mutually exclusive (default "Будь-де" = no
          backend filter). Sits directly under the search bar so the user
          can narrow before or after typing. */}
      <FilterChips
        options={LOCATION_OPTIONS.map((o) => ({
          value: o.value || 'any',
          label: o.label,
        }))}
        selected={location ? [location] : ['any']}
        onChange={(sel) => {
          const v = sel[0];
          setLocation(v === 'any' || !v ? '' : (v as LocationFilter));
        }}
        multi={false}
      />
      {results === null ? (
        <>
          <h2 className={styles.sectionTitle}>Або обери розділ</h2>
          <CategoryGrid categories={categories.filter((c) => c.parent_id !== null)} />
        </>
      ) : results.length === 0 ? (
        <EmptyState
          icon="search"
          title="Не знайшла"
          message="Спробуй інше слово, скинь місто або опиши потребу — бот знайде майстра."
          actions={
            <>
              <Button variant="secondary" onClick={() => navigate('/board/new')}>
                Шукаю майстра
              </Button>
              <Button variant="ghost" onClick={saveSearch}>
                Зберегти пошук
              </Button>
            </>
          }
        />
      ) : (
        <div className={styles.list}>
          {results.map((s) => (
            <SpecialistCard key={s.slug} specialist={s} />
          ))}
        </div>
      )}
      {results === null && q ? <Spinner center /> : null}
    </Layout>
  );
}
